import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { DownloadIcon, SignInIcon, UserPlusIcon } from '../components/Icons'
import { useAuth } from '../auth'

export function SignInPage() {
  const { signIn, register } = useAuth()
  const [params] = useSearchParams()
  // Set by ResetPasswordPage once a reset has gone through, so the first thing they see is that
  // it worked rather than an unexplained sign-in form.
  const justReset = params.get('reset') === '1'
  const [creating, setCreating] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (creating) {
        await register(email.trim(), password, displayName.trim())
      } else {
        await signIn(email.trim(), password)
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="landing">
      <div className="landing-top">
        <Intro />

        <form className="auth-card" onSubmit={submit}>
          <h1>{creating ? 'Create your account' : 'Sign in to My Tracker'}</h1>
          <p className="muted" style={{ marginTop: 8 }}>
            {creating
              ? 'Use the same account on your phone to upload activities automatically.'
              : 'The same account the Android app uploads your activities with.'}
          </p>

          {justReset && !creating && (
            <div className="notice">
              Your password has been changed. Sign in with the new one — you will need it on your
              phone as well, since the reset signed you out there too.
            </div>
          )}

          {creating && (
            <>
              <label htmlFor="displayName">Your name</label>
              <input
                id="displayName"
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </>
          )}

          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete={creating ? 'new-password' : 'current-password'}
            minLength={creating ? 8 : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {creating && (
            <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              At least 8 characters.
            </p>
          )}

          {/* Shown only when creating an account: the server refuses a sign-up that has not
              accepted, so this is the gate rather than a formality. */}
          {creating && (
            <label htmlFor="acceptTerms" className="consent">
              <input
                id="acceptTerms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
              />
              <span>
                I have read and accept the <Link to="/disclaimer">disclaimer</Link> and the{' '}
                <Link to="/privacy">privacy policy</Link>. I understand My Tracker is not a medical
                device and that its figures are estimates.
              </span>
            </label>
          )}

          {error && <div className="error">{error}</div>}

          <button className="primary" type="submit" disabled={busy || (creating && !acceptedTerms)}>
            {creating ? <UserPlusIcon /> : <SignInIcon />}
            {busy ? 'Just a moment…' : creating ? 'Create account' : 'Sign in'}
          </button>

          <button
            className="link"
            type="button"
            onClick={() => {
              setCreating(!creating)
              setError(null)
              setAcceptedTerms(false)
            }}
          >
            {creating ? 'I already have an account' : 'Create an account instead'}
          </button>

          {/* Only offered when signing in: it is no help to someone who has no account yet. */}
          {!creating && (
            <Link className="link" to="/forgot-password">
              Forgot your password?
            </Link>
          )}

          <AppDownload />
        </form>
      </div>

      <Features />
      <PhotoCredits />
    </div>
  )
}

/**
 * The pitch beside the form. Someone arriving here has usually not seen the Play listing, so the
 * page has to say what the app is before it asks for an email address.
 */
function Intro() {
  return (
    <section className="landing-intro">
      <h1 className="landing-title">Record your run. Keep the file.</h1>
      <p className="landing-lead">
        My Tracker is a training log for runs, walks, hikes and rides. The Android app records the
        activity with GPS and your heart-rate strap; this site is where you go through it
        afterwards — the route on a map, heart rate and pace across the distance, and every lap
        split.
      </p>

      <figure className="landing-photo">
        <img
          src="/photos/run-sunset.jpg"
          alt="A runner silhouetted against the sunset on a park path"
          width={1200}
          height={800}
        />
      </figure>

      <ul className="landing-points">
        <li>
          <strong>The file stays yours.</strong> Every activity is a standard TCX file, kept byte
          for byte as your phone wrote it, so you can export a run to Garmin Connect or Strava
          whenever you want it somewhere else.
        </li>
        <li>
          <strong>It copes with no signal.</strong> A run recorded out of range waits on the phone
          and uploads itself once you are back. Retrying never leaves you with duplicates.
        </li>
        <li>
          <strong>Your account, your call.</strong> Delete it from this site whenever you like and
          the runs, the files and the profile go with it.
        </li>
      </ul>
    </section>
  )
}

function Features() {
  return (
    <section className="landing-features">
      <article className="feature">
        <img
          src="/photos/run-path.jpg"
          alt="A runner on a shared path, with cyclists and walkers further back"
          width={900}
          height={1271}
          loading="lazy"
          style={{ objectPosition: 'center 38%' }}
        />
        <div className="feature-body">
          <h2>Four kinds of activity</h2>
          <p>
            Running, walking, hiking and cycling, chosen before you start and remembered for next
            time. Tapped the wrong one? Change it afterwards — the stored file is never rewritten.
          </p>
        </div>
      </article>

      <article className="feature">
        <img
          src="/photos/ride-road.jpg"
          alt="A cyclist riding on a country road with two more riders behind"
          width={900}
          height={601}
          loading="lazy"
        />
        <div className="feature-body">
          <h2>Heart rate from the strap you own</h2>
          <p>
            Any strap using the standard Bluetooth heart-rate service works — COOSPO, Polar, Garmin
            HRM-Dual, Wahoo and the rest. It pairs in the app, shows its battery, and reconnects on
            its own when the link drops mid-run.
          </p>
        </div>
      </article>
    </section>
  )
}

/**
 * The Pexels licence does not require attribution, but crediting the photographers costs a line
 * and records where the images came from for whoever edits this page next.
 */
function PhotoCredits() {
  return (
    <p className="photo-credits">
      Photographs by Cara Denison, Robert So and Pavel Danilyuk on{' '}
      <a href="https://www.pexels.com" rel="noreferrer noopener">
        Pexels
      </a>
      , used under the Pexels licence.
    </p>
  )
}

/**
 * The phone app is what actually records a run, so the website is where someone signs up and then
 * needs to go and get it. Served straight off this host rather than a store listing, which means
 * Android treats it as sideloaded — hence the unknown-sources note and the digest to check against.
 */
function AppDownload() {
  return (
    <div className="app-download">
      <strong>Get the Android app</strong>
      <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
        The phone app records the activity and uploads it here. Sign in with the same account and
        your activities appear on this site.
      </p>
      <a className="download-button" href="/mytracker.apk">
        <DownloadIcon />
        Download for Android
      </a>
      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        21 MB · Android 8.0 or newer. It does not come from the Play Store, so Android will ask you
        to allow installing from this source the first time.
      </p>
      <p className="checksum">SHA-256 c053eccbfe1b578cdb61654e2a3406451ae9630f8fb82166561f62368fd49dee</p>
    </div>
  )
}
